/**
 * The platform's CSV of a move run's list, read back into the list's own rows
 * (`GET /compacting/runs/{run_id}/export?kind=`, platform ADR 6194 / 9050; owner report 2026-11-02).
 *
 * The «Perché» workbook and its CSV used to walk the list a JSON page at a time (`GET …/moves`,
 * `GET …/ledger`), one page after another. The platform's export is the same list, row for row — its
 * docstring: "the columns are the page's fields, in its order; the rows are the page reads walked to
 * the end" — in ONE streamed request. As for the exit run lists (`features/exit/run-list-csv.ts`,
 * ADR 9069) and the run's cells, the platform contributes the ROWS; the file is still `src/export`'s,
 * through the same builder, so its columns, names, headers and both CSV dialects (ADR 9072) are as
 * before.
 *
 * **Read by name.** Every field of the page item is named once below, so a field the contract gains is a
 * compile error here until it is read; a column this version does not know is ignored, and a field the
 * file does not carry is a refusal ({@link MoveListFileError}) — never a row with a guessed value.
 * An empty cell is `null`, never a nought or an empty string; a decimal stays a string, digit for digit;
 * a text cell the platform neutralised against spreadsheet formulas is restored (the export builder
 * neutralises it again on the way out, once). Fewer rows than the platform stated (`X-Bata-Row-Count`,
 * platform ADR 6201) is an `ApiIncompleteFileError`: a short list is never written as the whole one.
 */

import { ApiIncompleteFileError, callOperationCsvFile, type CsvProgress } from '@/api';
import { exportMoveRun, type MoveLedgerItem, type MoveListItem, type MoveRunExportKind } from '@/api/generated';

import { parseCsv } from '../run-cells';

/** The file does not state the rows of the list: a column is missing, or a cell is not its column's type. */
export class MoveListFileError extends Error {
  constructor(public readonly detail: string) {
    super(`The platform's file of the move run's list cannot be read: ${detail}.`);
    this.name = 'MoveListFileError';
  }
}

/** `text`: a string. `nullable`: a string, `null` when empty. `integer`: a whole number. `optionalInteger`: one, or `null`. */
type FieldKind = 'text' | 'nullable' | 'integer' | 'optionalInteger';

const MOVE_FIELDS: { readonly [K in keyof MoveListItem]-?: FieldKind } = {
  article_key: 'nullable', carrying_cost_saved_amount: 'nullable', country_key: 'text', currency_code: 'nullable', decided_at: 'nullable',
  delta_units_sold: 'nullable', destination_store_key: 'nullable', disposition: 'nullable', disposition_reason_code: 'nullable',
  expected_price_amount: 'nullable', hold_cap: 'optionalInteger', hold_reason_code: 'nullable', hold_store_key: 'nullable',
  landed_cost_amount: 'nullable', landed_cost_provenance: 'nullable', lane_cost_amount: 'nullable', line_id: 'text', list_rank: 'integer',
  list_section: 'integer', net_benefit_amount: 'nullable', origin_store_key: 'nullable', owner_dc_units: 'optionalInteger',
  owner_snapshot_batch_id: 'nullable', owner_threshold: 'optionalInteger', purpose: 'nullable', rank: 'optionalInteger',
  realisation_rate: 'nullable', reason_code: 'nullable', refusal_code: 'nullable', refusal_detail: 'nullable', run_damage_amount: 'nullable',
  run_id: 'text', ship_week: 'nullable', size_key: 'nullable', source_ref: 'nullable', state: 'text', stock_owner: 'nullable',
  tier: 'optionalInteger', tier_kind: 'nullable', units: 'optionalInteger',
};

const LEDGER_FIELDS: { readonly [K in keyof MoveLedgerItem]-?: FieldKind } = {
  moves_cap_bound: 'optionalInteger', moves_deferred: 'integer', moves_retained: 'integer', store_key: 'text',
  units_cap_bound: 'optionalInteger', units_deferred: 'integer', units_sent: 'integer',
};

/** A text cell with the platform's formula guard taken off: `'=1+1` was written for `=1+1`. */
const FORMULA_GUARD = /^'(?=[=+\-@\t\r])/;

function cellOf(kind: FieldKind, name: string, cell: string, line: number): string | number | null {
  switch (kind) {
    case 'text': return cell.replace(FORMULA_GUARD, '');
    case 'nullable': return cell === '' ? null : cell.replace(FORMULA_GUARD, '');
    case 'integer':
    case 'optionalInteger':
      if (cell === '' && kind === 'optionalInteger') return null;
      if (!/^-?\d+$/.test(cell)) throw new MoveListFileError(`line ${line}: «${name}» is not a whole number`);
      return Number(cell);
    default: { const exhaustive: never = kind; throw new Error(String(exhaustive)); }
  }
}

/** The rows of a list's file, read by field name. Pure. */
function rowsOf<T>(text: string, fields: Readonly<Record<string, FieldKind>>): T[] {
  const [header, ...body] = parseCsv(text);
  if (header === undefined) throw new MoveListFileError('the file is empty');
  const at = new Map(header.map((name, index) => [name, index]));
  const present = Object.entries(fields).map(([name, kind]): readonly [string, FieldKind, number] => {
    const index = at.get(name);
    if (index === undefined) throw new MoveListFileError(`the column «${name}» is missing`);
    return [name, kind, index];
  });
  return body.map((cells, index) => {
    if (cells.length !== header.length) throw new MoveListFileError(`line ${index + 2} has ${cells.length} cells, the header ${header.length}`);
    const item: Record<string, string | number | null> = {};
    for (const [name, kind, column] of present) item[name] = cellOf(kind, name, cells[column]!, index + 2);
    return item as T;
  });
}

/** The lines of the platform's CSV of a move list (`moves`, `held`, `refusals`). Pure. */
export function moveLinesFromCsv(text: string): MoveListItem[] {
  return rowsOf<MoveListItem>(text, MOVE_FIELDS);
}

/** The store rows of the platform's CSV of the ledger. Pure. */
export function moveLedgerFromCsv(text: string): MoveLedgerItem[] {
  return rowsOf<MoveLedgerItem>(text, LEDGER_FIELDS);
}

/** One list's file, whole, refused when it holds fewer rows than the platform stated. */
async function readList<T>(runId: string, kind: MoveRunExportKind, parse: (text: string) => T[], onProgress?: CsvProgress): Promise<T[]> {
  const file = await callOperationCsvFile(exportMoveRun, { path: { run_id: runId }, query: { kind } }, {}, onProgress);
  const rows = parse(file.text);
  if (file.statedRows !== null && rows.length !== file.statedRows) throw new ApiIncompleteFileError(file.status, rows.length, file.statedRows);
  return rows;
}

/** A move list of the run — the one list (`moves`: proposed and held in the run's order) or its refused candidates — in ONE request. */
export function readMoveLines(runId: string, kind: Exclude<MoveRunExportKind, 'ledger'>, onProgress?: CsvProgress): Promise<MoveListItem[]> {
  return readList(runId, kind, moveLinesFromCsv, onProgress);
}

/** The run's store-week ledger in ONE request. */
export function readMoveLedger(runId: string): Promise<MoveLedgerItem[]> {
  return readList(runId, 'ledger', moveLedgerFromCsv);
}
